import {
  HHBaseTable,
  HHButton,
  HHLink,
  HHSelect,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { routes } from '../../../constants/strings/routes';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../../utils/bills-utils';
import { formatCurrency } from '../../../utils/currency-helpers';
import LoadingComponent from '../../utils/loading';
import { UnbilledTypeDataList } from '../bill-grid-components/bill-management-types';
import { buildBillManagementUrl } from '../bill-management/bill-management-navigator';
import { UnbilledFilterModelType } from './unbilled-report';

export enum GridColumnHeaderNames {
  CLIENT = 'Client',
  INSURER = 'Insurer',
  PARTNERSHIP = 'Partnership',
  CONTRACT_DATE_RANGE = 'Contract Date Range',
  BILLING_TYPE = 'Billing type',
  TOTAL_BILLS = 'Total Bills',
  TOTAL_UNBILLED_AMOUNT = 'Total Unbilled Amount',
}

export interface UnbilledGridProps {
  loading: boolean;
  hasError: boolean;
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  onPageSizeChange: (pageSize: number) => void;
  rows: UnbilledTypeDataList;
  filterParams: UnbilledFilterModelType;
}

export const UnbilledGrid = ({
  loading,
  hasError,
  page,
  setPage,
  pageSize,
  onPageSizeChange,
  rows,
  filterParams,
}: UnbilledGridProps): JSX.Element => {
  if (hasError) {
    return (
      <HHTypography
        hhVariant="subtitle1"
        color="error"
        sx={{ alignSelf: 'center' }}
      >
        Error fetching unbilled
      </HHTypography>
    );
  }

  if (loading) {
    return <LoadingComponent center />;
  }

  const isLastPage = rows.length < pageSize;

  return (
    <HHStack spacing={4}>
      {rows.length === 0 ? (
        <HHTypography hhVariant="muted">No unbilled results</HHTypography>
      ) : (
        <HHBaseTable
          headerRowBottomBorder="1px solid rgba(0, 0, 0, 0.12)"
          bodyRowBorder={{
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          }}
          bodyTypographyVariant="inherit"
          tableHeaders={[
            { value: GridColumnHeaderNames.CLIENT, align: 'left' },
            { value: GridColumnHeaderNames.INSURER, align: 'left' },
            { value: GridColumnHeaderNames.PARTNERSHIP, align: 'left' },
            { value: GridColumnHeaderNames.BILLING_TYPE, align: 'left' },
            { value: GridColumnHeaderNames.CONTRACT_DATE_RANGE, align: 'left' },
            { value: GridColumnHeaderNames.TOTAL_BILLS, align: 'left' },
            {
              value: GridColumnHeaderNames.TOTAL_UNBILLED_AMOUNT,
              align: 'left',
            },
            { value: '', align: 'left' },
          ]}
          bodyContent={rows.map(row => ({
            client: {
              value: row.client.name ? (
                <HHLink
                  color="primary"
                  href={`${routes.billing.home}/${routes.billing.clientPath}/${row.client.id}`}
                  target="_blank"
                  sx={{ textDecoration: 'none' }}
                >
                  {row.client.name}
                </HHLink>
              ) : (
                <div>Loading...</div>
              ),
            },
            insurer: {
              value: row.insurer.name ?? 'Loading...',
            },
            partnership: {
              value: row.partnership.name ?? '--',
            },
            billingType: {
              value:
                capitalizeFirstLetterAndSpaceSnakeString(
                  row.billingType.value ?? 'unavailable',
                ) ?? 'Loading...',
            },
            contractDateRange: {
              value: row.contractDateRange.value ?? '--',
            },
            billsCount: {
              value: row.billsCount ?? 'Loading...',
            },
            totalUnbilledAmount: {
              value: formatCurrency(row.unbilled ?? 0) ?? 'Loading...',
            },
            viewBills: {
              value: (
                // defaulting to subscriptions as unbilled only gets subscription unbilled
                <HHLink
                  color="primary"
                  href={buildBillManagementUrl({
                    source: 'subscriptions',
                    startDate: filterParams.startDate,
                    endDate: filterParams.endDate,
                    clientIds: row.client.id ? [row.client.id] : [],
                    insurerIds: row.insurer.id ? [row.insurer.id] : [],
                    partnershipIds: row.partnership.id
                      ? [row.partnership.id]
                      : [],
                    billingTypes: row.billingType.value
                      ? [row.billingType.value]
                      : [],
                    submitted: false,
                  })}
                  target="_blank"
                  sx={{ textDecoration: 'none' }}
                >
                  View Bills
                </HHLink>
              ),
            },
          }))}
        />
      )}
      <HHStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <HHSelect
          label="Items per page"
          initialSelectValue={pageSize}
          selectOptions={[10, 25, 50].map(value => ({
            value,
            displayName: value.toString(),
          }))}
          onChange={(e): void => {
            onPageSizeChange(Number(e.target.value));
          }}
          sx={{ minWidth: 10 }}
        />
        <HHStack direction="row">
          <HHButton
            hhVariant="text"
            disabled={page === 1}
            onClick={(): void => setPage(page - 1)}
          >
            Previous
          </HHButton>
          <HHButton
            hhVariant="text"
            disabled={isLastPage}
            onClick={(): void => setPage(page + 1)}
          >
            Next
          </HHButton>
        </HHStack>
      </HHStack>
    </HHStack>
  );
};
